/**
 * RecipePicker — dropdown at the top of the Metallic Epoxy calculator.
 *
 * Fetches active recipes on mount; on select, hydrates the calculator state
 * across primer / design coat / pigments / top coat / floor prep. Visible
 * on every step (currentStep 1..7) so installers can start-from-recipe or
 * swap look mid-quote without leaving the flow.
 *
 * Deliberately fault-tolerant — if the API is unreachable the dropdown
 * just doesn't render (rather than blocking the calculator).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  applyRecipeToArea,
  applyRecipeDesignCoat,
  computePigmentLineItems,
  buildPigmentSelectionsUpdate,
  recipeSummary,
} from './recipeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecipePicker({ state }) {
  const {
    primerAreas,
    setPrimerAreas,
    designCoatSelections,
    // useMetallicOrder exposes designCoatSelections but not its setter — we
    // use the initializer that fills every area, matching the auto-init
    // effect the hook already runs when entering step 4.
    pigmentSelections,
  } = state;

  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/proepoxy/recipes`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = await res.json();
        if (alive) setRecipes(Array.isArray(list) ? list : []);
      } catch (_e) {
        // Silent — dropdown just won't render.
        if (alive) setRecipes([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const totalM2 = useMemo(
    () => primerAreas.reduce((sum, a) => sum + (parseInt(a.m2) || 0), 0),
    [primerAreas]
  );

  const applyRecipe = async (recipe) => {
    if (!recipe) return;
    if (primerAreas.length === 0) {
      toast.error('Add an area first — recipes need at least one area with a size to fill.');
      return;
    }
    if (totalM2 <= 0) {
      toast.error('Enter each area\'s square metres in Step 1 first — the recipe needs it to size packs.');
      return;
    }

    setApplying(true);
    try {
      // 1) Hydrate every primer area — primer, top coat, floor prep, design coat intent.
      setPrimerAreas((prev) => prev.map((area) => applyRecipeToArea(area, recipe)));

      // 2) Compute + write pigments (metallicPowders) from the % blend against total m².
      const items = computePigmentLineItems({
        totalM2,
        pigmentGPerM2: recipe.pigment_g_per_m2 || 100,
        pigments: recipe.pigments || [],
      });
      const nextPigments = buildPigmentSelectionsUpdate(pigmentSelections, items);
      state.pigmentSelections.metallicPowders = nextPigments.metallicPowders; // ref mutate
      // Push through the exposed setters where available; the metallicPowders
      // list is authoritatively owned by pigmentSelections state inside the
      // hook. We drive updates by removing then re-adding through the
      // exported helpers to keep React state in sync.
      // Remove any current metallic powders, then add the recipe's line items.
      (pigmentSelections.metallicPowders || []).forEach((p) => state.removeMetallicPowder(p.color));
      items.forEach((it) => {
        // addMetallicPowder increments by 1; call it `quantity` times so we
        // end at the right count without touching internal state directly.
        for (let i = 0; i < it.quantity; i += 1) state.addMetallicPowder(it.color, it.size);
      });

      // 3) Fill design coat selections for every area.
      const nextDesign = applyRecipeDesignCoat(designCoatSelections, primerAreas, recipe);
      // designCoatSelections doesn't have a public setter; the hook auto-
      // hydrates on entering Step 4 for any area that isn't yet keyed. We
      // preemptively call `initializeDesignCoatForArea` / `updateDesignCoatProduct`
      // for each area to force the intended product_type.
      primerAreas.forEach((area) => {
        const m2 = parseInt(area.m2) || 0;
        if (m2 <= 0) return;
        state.initializeDesignCoatForArea(area.id, area.m2);
        state.updateDesignCoatProduct(area.id, nextDesign[area.id]?.productType || 'comfort', area.m2);
      });

      toast.success(`Applied "${recipe.name}"`, { description: recipeSummary(recipe) });
    } catch (e) {
      toast.error('Could not apply recipe', { description: String(e).slice(0, 200) });
    } finally {
      setApplying(false);
    }
  };

  if (loading || recipes.length === 0) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl px-3 py-2 mb-4 flex items-center gap-2 text-xs" data-testid="metallic-recipe-picker-empty">
        <Sparkles className="w-4 h-4 text-purple-600" />
        <span className="text-purple-700 font-medium">
          {loading ? 'Loading recipes…' : 'No recipes yet — staff can add signature looks from Staff → ProEpoxy Recipes.'}
        </span>
      </div>
    );
  }

  const selected = recipes.find((r) => r.id === selectedId);

  return (
    <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-amber-50 border border-purple-200 rounded-xl px-3 py-2.5 mb-4" data-testid="metallic-recipe-picker">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-purple-600" />
          </div>
          <span className="text-xs font-bold text-purple-900 uppercase tracking-wider">Start from a recipe</span>
        </div>
        <div className="relative flex-1">
          <select
            value={selectedId}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedId(id);
              const r = recipes.find((x) => x.id === id);
              if (r) applyRecipe(r);
            }}
            disabled={applying}
            className="w-full appearance-none bg-white border border-purple-300 text-sm font-medium text-gray-800 rounded-lg pl-3 pr-10 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400 cursor-pointer disabled:opacity-60"
            data-testid="metallic-recipe-picker-select"
          >
            <option value="">Load a signature look…</option>
            {recipes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}{r.pigments?.length ? ` — ${r.pigments.map((p) => `${p.color} ${p.percent}%`).join(' / ')}` : ''}
              </option>
            ))}
          </select>
          {applying ? (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500 animate-spin" />
          ) : (
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500 pointer-events-none" />
          )}
        </div>
      </div>
      {selected && !applying && (
        <div className="text-[11px] text-purple-800/80 mt-1.5 pl-9" data-testid="metallic-recipe-picker-summary">
          {recipeSummary(selected)}
        </div>
      )}
    </div>
  );
}
